<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import { contactAgendaName } from 'dashboard/helper/contactAgendaName';
import { aceleresContactDisplayName } from 'dashboard/helper/aceleresContactDisplayName';
import { inboxDisplayName } from 'dashboard/helper/inboxDisplayName';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize, useMediaQuery } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import RodizioCountdown from './RodizioCountdown.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import {
  whatsappPresenceLabel,
  ONLINE_LABEL,
  TYPING_LABEL,
  RECORDING_LABEL,
} from 'dashboard/helper/whatsappPresence';
import {
  CONTACT_CLASSIFICATION_ATTRIBUTE,
  resolveContactClassification,
} from 'dashboard/constants/contactClassifications';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

// Aceleres 2026-08-17: a foto do contato no cabecalho da conversa era 44px e
// o pedido foi 50px. O componente Avatar deriva TUDO desta medida — largura,
// altura, raio, o corpo das iniciais e a posicao do ponto de status —, entao
// mexer aqui basta: nao ha tamanho repetido em CSS para manter em dia.
//
// O MOBILE nao e afetado: `aceleres-overrides.css` fixa 48px nesta mesma foto
// dentro de `.acl-whatsapp-mobile-scope`, com `!important`, para caber nos
// 72px de altura daquele cabecalho. Este valor vale no desktop.
const AVATAR_SIZE = 40;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const mirroredAceleresChannels = ref([]);
const { width } = useElementSize(conversationHeader);
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const { isAWebWidgetInbox } = useInbox();
const { uiSettings, updateUISettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const typingUsersList = computed(() => {
  const userList = store.getters['conversationTypingStatus/getUserList'](
    props.chat?.id
  );
  return userList || [];
});

const isClientTyping = computed(() => {
  return typingUsersList.value.some(user => user?.type !== 'user');
});

const currentContact = computed(() => {
  const sender = props.chat?.meta?.sender || {};
  const contact = sender.id
    ? store.getters['contacts/getContact'](sender.id)
    : null;

  if (!contact?.id) return sender;

  const contactAttributes =
    contact.custom_attributes || contact.customAttributes || {};
  const hasHydratedClassification = Object.prototype.hasOwnProperty.call(
    contactAttributes,
    CONTACT_CLASSIFICATION_ATTRIBUTE
  );

  // A conversa traz o sender completo antes de a store de contatos terminar
  // de hidratar. Nesse intervalo, um contato parcial não pode sobrescrever a
  // classificação persistida e fazer o header voltar visualmente para Lead.
  return hasHydratedClassification ? contact : sender;
});
const contactClassification = computed(() =>
  resolveContactClassification(currentContact.value)
);
const isCommercialContact = computed(
  () => contactClassification.value.value === 'commercial_lead'
);

// O store de contatos é chaveado só por id, então o mesmo número salvo por dois
// vendedores com nomes diferentes é uma linha só. A inbox da conversa desempata.
// Ver helper/contactAgendaName.
const contactName = computed(() =>
  aceleresContactDisplayName(
    contactAgendaName(currentContact.value, props.chat?.inbox_id)
  )
);

// Presença envelhece sozinha quando o evento final da WAME se perde. O
// cabeçalho precisa de um tique próprio para não congelar "digitando...".
const nowTick = ref(Date.now());
let presenceTimer = null;

// O WhatsApp não empurra "online" de ninguém por iniciativa própria: só de JID
// assinado, e a assinatura devolve o estado atual em ~2s. O único gatilho que
// existia era o `update_last_seen`, que roda quando o atendente TROCA de
// conversa -- quem ficava parado na mesma tela via o rótulo decair para
// "Visto por último" com o contato ainda online. Este ping renova a assinatura
// enquanto a conversa está aberta, e o intervalo é menor que
// PRESENCE_FRESHNESS_MS de propósito.
const PRESENCE_REFRESH_MS = 100 * 1000;
let presenceRefreshTimer = null;

const refreshPresenceSubscription = () => {
  const id = props.chat?.id;
  if (currentContact.value?.blocked) return;
  if (
    !id ||
    document.hidden ||
    window.aceleresSessionTerminationStarted
  ) {
    return;
  }
  // Acessório por definição: se o Laravel ou a WAME estiverem fora, o painel
  // segue funcionando e o rótulo apenas não atualiza.
  ConversationApi.refreshPresence({ id }).catch(error => {
    if (error?.response?.status === 401) {
      store.dispatch('validityCheck').catch(() => {});
    }
  });
};

// Voltar para a aba é o momento em que o atendente OLHA o cabeçalho, e a
// presença dele pode estar até um ciclo atrasada (o ping não roda com a aba
// escondida, de propósito). Reassinar aqui faz o rótulo já chegar certo.
const onVisibilityChange = () => {
  if (!document.hidden) refreshPresenceSubscription();
};

onMounted(() => {
  presenceTimer = setInterval(() => {
    nowTick.value = Date.now();
  }, 250);
  refreshPresenceSubscription();
  presenceRefreshTimer = setInterval(
    refreshPresenceSubscription,
    PRESENCE_REFRESH_MS
  );
  document.addEventListener('visibilitychange', onVisibilityChange);
});
onBeforeUnmount(() => {
  if (presenceTimer) clearInterval(presenceTimer);
  presenceTimer = null;
  if (presenceRefreshTimer) clearInterval(presenceRefreshTimer);
  presenceRefreshTimer = null;
  document.removeEventListener('visibilitychange', onVisibilityChange);
});

// Trocar de conversa sem desmontar o cabeçalho precisa reassinar na hora --
// senão o contato novo fica sem presença até o próximo tique de 100s.
watch(
  () => props.chat?.id,
  id => {
    if (id) refreshPresenceSubscription();
  }
);

// Pedido 02/09/2026: o "digitando..." SAIU do cabeçalho. Quem mostra que a
// pessoa está digitando passou a ser a bolha de três pontos no fim da conversa
// (MessagesView), como no WhatsApp. Aqui ficava também o broadcast de typing
// do Laravel, que servia só para acender esse rótulo mais depressa que o
// atributo do contato — sem rótulo para acender, ele deixou de ter função e
// saiu junto, em vez de continuar a ser calculado para nada.

// A presença exibida abaixo do nome é exclusivamente a recebida do WhatsApp.
// Sem `last_seen` da integração, não há fallback para timestamp de mensagem,
// atualização da conversa ou qualquer ação do atendimento — quem cobre esse
// caso é a linha de última atividade, que é outra informação e diz isso no
// próprio texto.
const presenceLabel = computed(() => {
  const daIntegracao = whatsappPresenceLabel(
    currentContact.value,
    nowTick.value
  );

  // "gravando áudio..." continua aqui: é o único dos dois estados de segundos
  // que o cabeçalho ainda anuncia, e a bolha de pontos não distingue gravar de
  // digitar (o broadcast que a acende é um liga/desliga).
  if (daIntegracao === RECORDING_LABEL) return daIntegracao;

  // Pedido 02/09/2026: digitar deixa de ter rótulo próprio. Vira "online",
  // que é o que a mesma presença diria assim que a pessoa parasse de digitar —
  // e não uma linha vazia, que faria o cabeçalho piscar a cada tecla.
  if (daIntegracao === TYPING_LABEL) return ONLINE_LABEL;

  return daIntegracao;
});

// "gravando áudio..." ganha reticências animadas, como no WhatsApp. Os três
// pontos saem do TEXTO e viram elementos -- deixá-los na string faria a
// animação empurrar a largura do cabeçalho a cada quadro. ("digitando..." está
// nesta lista até 02/09/2026; saiu com o rótulo.)
const isAnimatedPresence = computed(
  () => presenceLabel.value === RECORDING_LABEL
);

const presenceLabelText = computed(() =>
  isAnimatedPresence.value
    ? presenceLabel.value.replace(/\.{3}$/, '')
    : presenceLabel.value
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

watch(
  () => props.chat?.inbox_id,
  async inboxId => {
    if (!inboxId || inbox.value?.id) return;
    await store.dispatch('inboxes/fetch', inboxId);
  },
  { immediate: true }
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const rodizioAssignedAt = computed(() => {
  // Aceleres: timer só enquanto o lead ainda circula no rodízio — conversa
  // respondida/ancorada (rodizio_locked), ciclo esgotado (rodizio_exhausted)
  // ou não-aberta não mostram countdown.
  const attrs = props.chat?.additional_attributes || {};
  if (attrs.rodizio_locked || attrs.rodizio_exhausted) return null;
  if (props.chat?.status && props.chat.status !== 'open') return null;
  return attrs.rodizio_assigned_at || null;
});

const rodizioIdleMins = computed(() => {
  const attrs = props.chat?.additional_attributes || {};
  const value = Number(attrs.rodizio_idle_minutes || attrs.rodizio_idle_mins);
  return Number.isFinite(value) && value > 0 ? value : null;
});

// ─── Aceleres: header clicável abre o painel do contato (estilo WhatsApp) ───
function openContactPanel(e) {
  // não dispara quando o clique foi num botão/ação do próprio header
  if (e?.target?.closest('button, a, [role="button"], input')) return;
  updateUISettings({
    is_contact_sidebar_open: !uiSettings.value.is_contact_sidebar_open,
    is_copilot_panel_open: false,
  });
}

// ─── Aceleres: identidade do canal (ícone/cor) ───
const CHANNEL_BRAND = {
  [INBOX_TYPES.WHATSAPP]: { icon: 'i-ri-whatsapp-fill', color: '#25D366' },
  [INBOX_TYPES.API]: { icon: 'i-ri-whatsapp-fill', color: '#25D366' },
  [INBOX_TYPES.FB]: { icon: 'i-ri-messenger-fill', color: '#0084FF' },
  [INBOX_TYPES.INSTAGRAM]: { icon: 'i-ri-instagram-fill', color: '#E1306C' },
  [INBOX_TYPES.TELEGRAM]: { icon: 'i-ri-telegram-fill', color: '#229ED9' },
  [INBOX_TYPES.EMAIL]: { icon: 'i-ri-mail-fill', color: '#64748B' },
  [INBOX_TYPES.WEB]: { icon: 'i-ri-global-fill', color: '#64748B' },
  [INBOX_TYPES.VOICE]: { icon: 'i-ri-phone-fill', color: '#64748B' },
};
const channelBrand = computed(
  () =>
    CHANNEL_BRAND[inbox.value?.channel_type] || {
      icon: 'i-ri-chat-1-fill',
      color: '#94A3B8',
    }
);
const cleanInstanceName = value =>
  (value || '')
    .replace(/ · [a-f0-9]{6}$/i, '')
    .replace(/\s*[([{]?\s*vo[cç]ê\s*[)\]}]?$/i, '')
    .trim();

const normalizeComparable = value =>
  String(value || '')
    .trim()
    .replace(/^#/, '')
    .toLowerCase();

const normalizeDigits = value => String(value || '').replace(/\D/g, '');

const isPresentValue = value =>
  value !== undefined && value !== null && String(value).trim();

const isObjectRecord = value =>
  value && typeof value === 'object' && !Array.isArray(value);

const valueContainers = source => {
  if (!isObjectRecord(source)) return [];

  const seen = new Set();
  const containers = [];
  const collect = record => {
    if (!isObjectRecord(record) || seen.has(record)) return;
    seen.add(record);
    containers.push(record);

    [
      'attributes',
      'additional_attributes',
      'additionalAttributes',
      'provider_config',
      'providerConfig',
      'channel',
      'inbox',
      'instance',
      'integration',
      'social_channel',
      'socialChannel',
      'whatsapp',
      'whatsapp_instance',
      'whatsappInstance',
      'meta',
      'metadata',
    ].forEach(key => collect(record[key]));
  };

  collect(source);
  return containers;
};

const presentValues = (source, keys) =>
  valueContainers(source)
    .flatMap(container => keys.map(key => container?.[key]))
    .filter(isPresentValue);

const firstPresentValue = (source, keys) => {
  return presentValues(source, keys)[0] || '';
};

const normalizedValues = (source, keys, normalizer = normalizeComparable) =>
  presentValues(source, keys).map(normalizer).filter(Boolean);

const hasSharedValue = (leftValues, rightValues) =>
  leftValues.some(value => rightValues.includes(value));

const inboxAttributes = computed(() => ({
  ...(inbox.value?.provider_config || {}),
  ...(inbox.value?.channel?.provider_config || {}),
  ...(inbox.value?.additional_attributes || {}),
  ...(inbox.value?.channel?.additional_attributes || {}),
}));

const conversationAttributes = computed(() => ({
  ...(props.chat?.additional_attributes || {}),
  ...(props.chat?.additionalAttributes || {}),
}));

const localValueSource = computed(() => ({
  ...inboxAttributes.value,
  ...conversationAttributes.value,
  id: inbox.value?.id,
  channel_id: inbox.value?.channel_id,
  name: inbox.value?.name,
  phone_number: inbox.value?.phone_number,
}));

const cleanNormalizedNames = (source, keys) =>
  normalizedValues(source, keys, value =>
    normalizeComparable(cleanInstanceName(value))
  );

const cleanNormalizedPhones = (source, keys) =>
  normalizedValues(source, keys, normalizeDigits).filter(
    value => value.length >= 8
  );

const localInboxIdKeys = ['chatwoot_inbox_id', 'inbox_id', 'id'];

const localChannelIdKeys = ['channel_id'];

const integrationCodeKeys = [
  'integration_code',
  'integrationCode',
  'integration_number',
  'integrationNumber',
  'integration_phone',
  'integrationPhone',
  'integration_id',
  'integrationId',
  'wa_integration_code',
  'waIntegrationCode',
  'wa_integration_number',
  'waIntegrationNumber',
  'code',
  'codigo',
  'codigo_integracao',
  'codigoIntegracao',
  'aceleres_instance_code',
  'aceleresInstanceCode',
  'evolution_instance_code',
  'evolutionInstanceCode',
  'instance_code',
  'instanceCode',
  'short_code',
  'shortCode',
  'external_id',
  'externalId',
  'identifier',
];

const remoteChatwootIdKeys = [
  'chatwoot_inbox_id',
  'chatwootInboxId',
  'inbox_id',
  'inboxId',
  'channel_inbox_id',
  'channelInboxId',
];

const remoteChannelIdKeys = [
  'chatwoot_channel_id',
  'chatwootChannelId',
  'channel_id',
  'channelId',
];

const instanceIdKeys = [
  'aceleres_instance_id',
  'aceleresInstanceId',
  'evolution_instance_id',
  'evolutionInstanceId',
  'whatsapp_instance_id',
  'whatsappInstanceId',
  'wa_instance_id',
  'waInstanceId',
  'instance_id',
  'instanceId',
];

const instanceCodeKeys = [...integrationCodeKeys, ...instanceIdKeys];

const mirrorNameKeys = [
  'aceleres_instance_name',
  'aceleresInstanceName',
  'evolution_instance_name',
  'evolutionInstanceName',
  'instance_name',
  'instanceName',
  'whatsapp_instance_name',
  'whatsappInstanceName',
  'name',
  'label',
  'title',
];

const mirrorPhoneKeys = [
  'display_phone_number',
  'displayPhoneNumber',
  'display_number',
  'displayNumber',
  'phone_number',
  'phoneNumber',
  'whatsapp_number',
  'whatsappNumber',
  'whatsapp_phone',
  'whatsappPhone',
  'wa_number',
  'waNumber',
  'wa_phone',
  'waPhone',
  'instance_number',
  'instanceNumber',
  'instance_phone',
  'instancePhone',
  'connected_number',
  'connectedNumber',
  'connected_phone',
  'connectedPhone',
  'logged_number',
  'loggedNumber',
  'logged_phone',
  'loggedPhone',
  'logged_in_number',
  'loggedInNumber',
  'logged_in_phone',
  'loggedInPhone',
  'number',
  'numero',
  'número',
  'telefone',
  'phone',
];

const mirrorDisplayNumberKeys = [...mirrorPhoneKeys];

const mirrorStatusKeys = [
  'status',
  'connection_status',
  'connectionStatus',
  'state',
  'connected',
  'logged',
  'logged_in',
  'loggedIn',
];

const channelTypeKeys = [
  'type',
  'channel_type',
  'channelType',
  'provider',
  'platform',
  'service',
  'kind',
];

const normalizedTypeValues = source =>
  normalizedValues(source, channelTypeKeys).map(value =>
    value.replace(/^channel::/i, '')
  );

const isConnectedMirror = source => {
  const values = normalizedValues(source, mirrorStatusKeys);
  if (!values.length) return true;
  return values.some(value =>
    [
      'connected',
      'conectado',
      'online',
      'open',
      'active',
      'true',
      'logged',
    ].some(status => value.includes(status))
  );
};

const isWhatsappMirror = source => {
  const values = normalizedTypeValues(source);
  const brandValues = [
    ...cleanNormalizedNames(source, mirrorNameKeys),
    ...normalizedValues(source, integrationCodeKeys),
  ];
  return [...values, ...brandValues].some(value => value.includes('whatsapp'));
};

const normalizeMirrorRecords = data => {
  const candidates = [
    data?.channels,
    data?.data,
    data?.instances,
    data?.whatsapp_instances,
    data?.whatsappInstances,
    data?.integrations,
    data?.records,
  ];

  if (Array.isArray(data?.data?.channels)) candidates.push(data.data.channels);
  if (Array.isArray(data?.data?.instances)) {
    candidates.push(data.data.instances);
  }
  if (Array.isArray(data?.data?.whatsapp_instances)) {
    candidates.push(data.data.whatsapp_instances);
  }
  if (Array.isArray(data?.data?.whatsappInstances)) {
    candidates.push(data.data.whatsappInstances);
  }

  return candidates.find(Array.isArray) || [];
};

const firstDisplayNumber = source => {
  const value = firstPresentValue(source, mirrorDisplayNumberKeys);
  return normalizeDigits(value).length >= 8 ? value : '';
};

const formatInstanceCode = value => {
  const code = String(value || '').trim();
  if (!code) return '';
  return code.startsWith('#') ? code : `#${code}`;
};

const mirroredInstance = computed(() => {
  const currentInbox = inbox.value;
  if (!currentInbox?.id || !mirroredAceleresChannels.value.length) return null;

  const localSource = localValueSource.value;
  const localIntegrationCodes = normalizedValues(
    localSource,
    integrationCodeKeys
  );
  const localInboxIds = normalizedValues(localSource, localInboxIdKeys);
  const localChannelIds = normalizedValues(localSource, localChannelIdKeys);
  const localInstanceIds = normalizedValues(localSource, instanceIdKeys);
  const localNames = cleanNormalizedNames(localSource, mirrorNameKeys);
  const localPhones = cleanNormalizedPhones(localSource, mirrorPhoneKeys);

  const matchedChannel =
    mirroredAceleresChannels.value.find(channel => {
      if (
        localIntegrationCodes.length &&
        hasSharedValue(
          normalizedValues(channel, integrationCodeKeys),
          localIntegrationCodes
        )
      ) {
        return true;
      }

      if (
        localInboxIds.length &&
        hasSharedValue(
          normalizedValues(channel, remoteChatwootIdKeys),
          localInboxIds
        )
      ) {
        return true;
      }

      if (
        localChannelIds.length &&
        hasSharedValue(
          normalizedValues(channel, remoteChannelIdKeys),
          localChannelIds
        )
      ) {
        return true;
      }

      if (
        localInstanceIds.length &&
        hasSharedValue(
          normalizedValues(channel, instanceIdKeys),
          localInstanceIds
        )
      ) {
        return true;
      }

      if (
        localNames.length &&
        hasSharedValue(
          cleanNormalizedNames(channel, mirrorNameKeys),
          localNames
        )
      ) {
        return true;
      }

      return (
        localPhones.length &&
        hasSharedValue(
          cleanNormalizedPhones(channel, mirrorDisplayNumberKeys),
          localPhones
        )
      );
    }) || null;

  if (matchedChannel) return matchedChannel;

  const whatsappMirrors = mirroredAceleresChannels.value.filter(
    channel => isWhatsappMirror(channel) && isConnectedMirror(channel)
  );
  return whatsappMirrors.length === 1 ? whatsappMirrors[0] : null;
});

const localInstanceNumber = computed(() => {
  const attrs = inboxAttributes.value;
  const value =
    attrs.display_phone_number ||
    attrs.phone_number ||
    attrs.whatsapp_number ||
    attrs.phone ||
    inbox.value?.phone_number ||
    '';

  return normalizeDigits(value).length >= 8 ? value : '';
});

const mirroredInstanceNumber = computed(() => {
  return firstDisplayNumber(mirroredInstance.value);
});

const instanceNumber = computed(
  () => mirroredInstanceNumber.value || localInstanceNumber.value
);

const localInstanceCode = computed(() =>
  formatInstanceCode(
    firstPresentValue(localValueSource.value, instanceCodeKeys)
  )
);

const mirroredInstanceCode = computed(() =>
  formatInstanceCode(
    firstPresentValue(mirroredInstance.value, instanceCodeKeys)
  )
);

const instanceCode = computed(
  () => localInstanceCode.value || mirroredInstanceCode.value
);

// A identificação operacional só é completa quando traz ID e telefone. Não
// exibimos apenas o #ID em registros legados/parciais, pois isso induz o agente
// a confundir o identificador interno com o número conectado.
const hasCompleteInstanceIdentity = computed(() =>
  Boolean(instanceCode.value && instanceNumber.value)
);

const resolveAceleresBase = () => resolveAceleresApiBase();

const loadAceleresMirror = async () => {
  try {
    const response = await fetch(
      `${resolveAceleresBase()}/api/integracoes/social-channels`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success !== false) {
      mirroredAceleresChannels.value = normalizeMirrorRecords(data);
    }
  } catch {
    mirroredAceleresChannels.value = [];
  }
};

const instanceName = computed(() => {
  const attrs = inboxAttributes.value;
  return inboxDisplayName(
    firstPresentValue(mirroredInstance.value, mirrorNameKeys) ||
      attrs.aceleres_instance_name ||
      attrs.evolution_instance_name ||
      attrs.instance_name ||
      attrs.whatsapp_instance_name ||
      cleanInstanceName(inbox.value?.name) ||
      t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')
  );
});

const instanceTitleParts = computed(() =>
  [instanceName.value, instanceCode.value, instanceNumber.value].filter(
    (value, index, values) => value && values.indexOf(value) === index
  )
);

const instanceTitle = computed(() => instanceTitleParts.value.join(' · '));

onMounted(() => {
  loadAceleresMirror();
});

// Aceleres: funil / transferir / IA agora ficam dentro do dropdown de MoreActions
// (ver MoreActions.vue). O header só carrega identidade do contato + SLA + countdown.
</script>

<template>
  <div
    ref="conversationHeader"
    class="acl-conversation-header relative z-[100] flex h-16 flex-none flex-col items-center justify-between gap-3 w-full min-w-0 bg-white px-3 py-0 cursor-pointer xl:flex-row"
    @click="openContactPanel"
  >
    <div
      class="acl-conversation-header__identity flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        :icon-only="isMobileViewport"
        class="acl-conversation-header__back ltr:mr-2 rtl:ml-2 max-md:[&_.acl-back-button__icon]:!size-[22px] max-md:[&_.acl-back-button__icon]:!text-[22px]"
      />
      <div class="acl-conversation-header__avatar relative flex-shrink-0">
        <Avatar
          class="acl-conversation-contact-avatar"
          :name="contactName"
          :src="currentContact.thumbnail"
          :size="AVATAR_SIZE"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <span
          class="absolute -bottom-0.5 -right-0.5 size-4 rounded-full bg-white dark:bg-n-solid-2 flex items-center justify-center shadow-sm"
        >
          <span
            class="size-3"
            :class="[channelBrand.icon]"
            :style="{ color: channelBrand.color }"
          />
        </span>
      </div>
      <div
        class="acl-conversation-header__contact flex flex-col justify-center gap-0.5 min-w-0 ml-3 overflow-hidden rtl:ml-0 rtl:mr-3"
      >
        <div class="flex items-center gap-1.5 min-w-0">
          <span
            class="text-[14px] font-normal truncate leading-tight text-n-slate-12"
          >
            {{ contactName }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="16"
            class="text-n-amber-10 my-0 mx-0 min-w-[16px] flex-shrink-0"
            icon="warning"
          />
          <span
            v-if="isSnoozed"
            class="text-sm font-normal text-n-amber-10 truncate"
          >
            {{ snoozedDisplayText }}
          </span>
        </div>
        <!-- Aceleres: a segunda linha do cabecalho, como no WhatsApp --
             SEMPRE abaixo do nome, nunca ao lado dele.
             Mostra EXCLUSIVAMENTE o ultimo online do cliente vindo da
             integracao (online / gravando audio / visto por ultimo), OU
             "digitando..." quando o cliente está digitando (02/09/2026). -->
        <div
          v-if="isClientTyping || presenceLabel"
          class="text-xs font-normal leading-tight text-n-slate-10 truncate"
        >
          <span data-testid="whatsapp-last-activity" class="acl-presence-line">
            <span v-if="isClientTyping">
              {{ $t('CONVERSATION.TYPING_LABEL') }}
            </span>
            <span v-else>{{ presenceLabelText }}</span>
            <span
              v-if="isClientTyping || isAnimatedPresence"
              class="acl-presence-dots"
            >
              <i />
              <i />
              <i />
            </span>
          </span>
        </div>
      </div>
    </div>
    <div
      class="acl-conversation-header__actions flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId && isCommercialContact"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <RodizioCountdown
        v-if="rodizioAssignedAt && isCommercialContact"
        :assigned-at="rodizioAssignedAt"
        :idle-mins="rodizioIdleMins"
        class="hidden md:inline-flex"
      />

      <div
        class="acl-conversation-header__instance-actions"
        :class="{
          'acl-conversation-header__instance-actions--empty': !inbox?.id,
        }"
      >
        <!-- card da instância (canal • nome • número).
             Aviso de instância offline agora é a topbar vermelha global
             (aceleres-inbox-offline-banner.js) — card fica sempre neutro. -->
        <div
          v-if="inbox?.id"
          class="acl-conversation-header__instance-card flex items-center gap-2 px-3 py-1 rounded-xl bg-n-alpha-1 border border-n-weak max-w-[260px]"
          :title="instanceTitle"
        >
          <span class="acl-conversation-header__instance-icon-wrap">
            <span
              class="acl-conversation-header__instance-icon size-4 flex-shrink-0"
              :class="[channelBrand.icon]"
              :style="{ color: channelBrand.color }"
            />
          </span>
          <div class="flex flex-col leading-tight min-w-0">
            <span
              class="acl-conversation-header__instance-name text-xs font-normal truncate text-n-slate-12"
            >
              {{ instanceName }}
            </span>
            <div
              v-if="hasCompleteInstanceIdentity"
              class="acl-conversation-header__instance-meta flex items-center gap-1 text-[11px] truncate"
            >
              <span class="font-normal text-n-teal-10 truncate">
                {{ instanceCode }}
              </span>
              <span class="text-n-slate-9" aria-hidden="true"> · </span>
              <span class="text-n-slate-11 truncate">
                {{ instanceNumber }}
              </span>
            </div>
          </div>
        </div>

        <!-- Aceleres 2026-08-19: no telemovel o cartao da conexao sai do
             cabecalho (regra em aceleres-overrides.css) e o texto dele vai
             para dentro deste menu. O calculo continua a viver AQUI, que e
             quem sabe resolver o nome da conexao; o menu so recebe pronto. -->
        <MoreActions
          :conversation-id="currentChat.id"
          :instance-label="instanceTitle"
          :instance-icon="channelBrand.icon"
          :contact="currentContact"
          class="acl-conversation-header__menu"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Reticências animadas do "digitando...", como no WhatsApp. A largura é fixa
   (3 pontos sempre ocupam o mesmo espaço) para o texto ao lado não tremer. */
/* inline-flex: o espaco em branco entre os dois <span> do template vira um no
   de texto que o flex descarta, entao os pontos colam no texto sem truque de
   `><` no HTML. */
.acl-presence-line {
  display: inline-flex;
  align-items: baseline;
}

.acl-presence-dots {
  display: inline-flex;
  align-items: flex-end;
  gap: 1px;
  margin-inline-start: 1px;
}

.acl-presence-dots i {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.25;
  animation: acl-presence-dot 1.2s infinite ease-in-out;
}

.acl-presence-dots i:nth-child(2) {
  animation-delay: 0.2s;
}

.acl-presence-dots i:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes acl-presence-dot {
  0%,
  60%,
  100% {
    opacity: 0.25;
  }
  30% {
    opacity: 1;
  }
}

/* Quem pediu para reduzir movimento não precisa dos pontos pulsando. */
@media (prefers-reduced-motion: reduce) {
  .acl-presence-dots i {
    animation: none;
    opacity: 0.6;
  }
}

.acl-conversation-header__instance-card {
  min-height: 40px;
  max-width: 280px;
  gap: 8px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  box-shadow: none;
}

.acl-conversation-header__instance-actions {
  position: relative;
  z-index: 120;
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  gap: 4px;
  padding: 2px;
  overflow: visible;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.acl-conversation-header__instance-actions--empty {
  border: 0;
  box-shadow: none;
}

.acl-conversation-header__instance-icon-wrap {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  background: transparent;
}

.acl-conversation-header__instance-icon {
  width: 20px;
  height: 20px;
  font-size: 20px;
}

.acl-conversation-header__instance-name {
  color: #111827;
  font-size: 12px;
  line-height: 1.2;
}

.acl-conversation-header__instance-meta {
  color: rgba(0, 0, 0, 0.6);
  font-size: 11px;
  line-height: 1.2;
}

.acl-conversation-header__menu :deep(.acl-conversation-more-actions-trigger) {
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  box-shadow: none;
}

.acl-conversation-header__menu {
  position: relative;
  z-index: 130;
}

.acl-conversation-header__menu
  :deep(.acl-conversation-more-actions-trigger:hover),
.acl-conversation-header__menu
  :deep(.acl-conversation-more-actions-trigger:focus-visible) {
  background: rgba(15, 23, 42, 0.04);
  outline: none;
}

.acl-conversation-header__menu
  :deep(.acl-conversation-more-actions-trigger [class*='i-']) {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  font-size: 20px;
}
</style>
